import * as React from 'react';
import { Box, IconButton, Popper, PopperProps, Stack, styled, TextField, useTheme } from '@mui/material';
import { Controller } from 'react-hook-form';
import { FormControl, FormHelperText, FormLabel as MuiFormLabel } from '@mui/material';
import Colors from '@constants/Colors';
import { Autocomplete } from './AutoComplete';
import Image from '@components/Image';
import { GET_BRANDS } from '@gql/brand';
import { useQuery } from '@apollo/client';
import { GET_PRO } from '@gql/pros';

interface Brand {
    _id: string;
    brandName: string;
    brandLogo?: {
        url: string;
    };
}

const FormLabel = styled(MuiFormLabel)(() => ({
    marginBottom: '4px',
}));

const EscHint = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    padding: '4px 8px',
    textAlign: 'right',
    color: theme.palette.text.secondary,
    fontSize: '12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
}));

const EscHintIcon = styled('i')(({ theme }) => ({
    marginRight: 4,
    fontSize: 12,
    color: theme.palette.text.secondary,
}));

const PopperWithEscHint = (props: PopperProps) => {
    const { children, ...other } = props;
    return (
        <Popper {...other}>
            <div>
                <EscHint>
                    <EscHintIcon className='fi fi-br-info' />
                    Appuyez sur Esc pour fermer la liste.
                </EscHint>
                {children as React.ReactNode}
            </div>
        </Popper>
    );
};

const EndAdornmentContainer = styled(Stack)(({ theme }) => ({
    position: 'absolute',
    right: 0,
    top: '8px',
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.75),
}));

const LogoAdornment = styled('div')(({ theme }) => ({
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 22,
    height: 22,
    zIndex: 1,
    '& img': {
        objectFit: 'cover',
        width: '100%',
        height: '100%',
    },
}));

export default function AutoCompleteBrands({ name, label, control, proId, ...props }) {
    const theme = useTheme();
    const [isOpen, setIsOpen] = React.useState(false);
    const brandQuery = useQuery(GET_BRANDS, {
        variables: {
            proId: proId,
            pagination: { limit: 1000 },
        },
        skip: !proId,
    });
    const proQuery = useQuery(GET_PRO, {
        variables: { proId: proId },
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-first',
        skip: !proId,
    });

    const error = React.useMemo(() => {
        return proQuery.error || brandQuery.error;
    }, [proQuery.error, brandQuery.error]);

    const loading = React.useMemo(() => {
        return proQuery.loading || brandQuery.loading;
    }, [proQuery.loading, brandQuery.loading]);

    const data = React.useMemo(() => {
        const brandFromPro = {
            _id: null,
            brandName: proQuery.data?.getPro?.commercialName,
            brandLogo: {
                url: proQuery.data?.getPro?.logo,
            },
            brandBanner: {
                url: proQuery.data?.getPro?.photo,
            },
            brandVideos: proQuery.data?.getPro?.brandVideos,
            brandPhotos: proQuery.data?.getPro?.photos?.map(item => ({ url: item })),
        };

        return [brandFromPro, ...(brandQuery.data?.getBrands?.docs || [])];
    }, [brandQuery.data?.getBrands?.docs, proQuery.data?.getPro]);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => (
                <FormControl sx={{ flex: 1, minWidth: 200 }} fullWidth error={!!fieldError}>
                    <FormLabel htmlFor={name}>
                        {label}
                    </FormLabel>
                    <Autocomplete
                        id="brand-select-demo"
                        disablePortal
                        PopperComponent={PopperWithEscHint}
                        options={data}
                        disabled={loading || !!error || props.viewOnly}
                        autoHighlight
                        open={isOpen}
                        value={data?.find(item => {
                            if (!value) return item._id === null;
                            return item._id === value;
                        }) || null}
                        onChange={(event, newValue: Brand | null) => {
                            onChange(newValue?._id, newValue);
                            props.onChange?.(newValue?._id, newValue);
                        }}
                        onOpen={() => {
                            setIsOpen(true);
                        }}
                        onClose={() => {
                            setIsOpen(false);
                        }}
                        getOptionLabel={(option: Brand) => option.brandName || ''}
                        isOptionEqualToValue={(option: Brand, val: Brand) => option._id === val._id}
                        renderOption={(props, option: Brand) => (
                            <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                <Image
                                    variant="circle"
                                    src={option?.brandLogo?.url}
                                    alt={option.brandName}
                                    containerStyle={{ width: 22, height: 22, marginRight: 8 }}
                                    imgStyle={{ objectFit: 'cover' }}
                                />
                                {option.brandName}
                            </Box>
                        )}
                        renderInput={params => {
                            const selected: Brand | null = data?.find(item => {
                                if (!value) return item._id === null;
                                return item._id === value;
                            }) || null;

                            return (
                                <div style={{ position: 'relative' }}>
                                    {selected?.brandLogo?.url && <LogoAdornment>
                                        <Image
                                            variant="circle"
                                            src={selected?.brandLogo?.url}
                                            alt=""
                                            containerStyle={{ width: '100%', height: '100%' }}
                                        />
                                    </LogoAdornment>}
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                rowGap: '8px',
                                                paddingLeft: '42px !important',
                                            },
                                            '& .MuiInputBase-input::placeholder': {
                                                color: fieldError ? 'error.main' : 'inherit',
                                                opacity: 1,
                                            },
                                        }}
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <EndAdornmentContainer
                                                    flexDirection={'row'}
                                                    alignItems={'center'}>
                                                    {isOpen ? (
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                width: 28,
                                                                height: 28,
                                                            }}
                                                            onClick={() => {
                                                                setIsOpen(false);
                                                            }}>
                                                            <i
                                                                className="fi-br-check"
                                                                style={{
                                                                    fontSize: 14,
                                                                    color: theme.palette.primary.main,
                                                                }}
                                                            />
                                                        </IconButton>
                                                    ) : (
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                width: 28,
                                                                height: 28,
                                                            }}
                                                            onClick={() => {
                                                                setIsOpen(true);
                                                            }}>
                                                            <i
                                                                className="fi-br-caret-down"
                                                                style={{
                                                                    fontSize: 16,
                                                                    color: Colors.NEUTRAL_GRAY,
                                                                }}
                                                            />
                                                        </IconButton>
                                                    )}
                                                </EndAdornmentContainer>
                                            ),
                                        }}
                                    />
                                </div>
                            );
                        }}
                    />
                    {fieldError && <FormHelperText error>{fieldError.message}</FormHelperText>}
                </FormControl>
            )}
        />
    );
}
